import { useMemo } from 'react';
import { VisitState, VisitsFiltersState } from 'store/visits/types';

interface UseVisitsEmptyStateParams {
  visits: VisitState[];
  filters: VisitsFiltersState;
  selectedTimeFrame: string | null;
  endDate: string | null;
}

interface UseVisitsEmptyStateReturn {
  validVisits: VisitState[];
  isEmpty: boolean;
  areFiltersActive: boolean;
}

export const useVisitsEmptyState = ({
  visits,
  filters,
  selectedTimeFrame,
  endDate,
}: UseVisitsEmptyStateParams): UseVisitsEmptyStateReturn => {
  // Filter out visits with null data
  const validVisits = useMemo(() => {
    return visits.filter((visit) => visit.data !== null);
  }, [visits]);

  // Check if any filters are active
  const areFiltersActive = useMemo(() => {
    const hasSearchFilter = filters.search.trim().length > 0;
    const hasStatusFilter = filters.status.length > 0;
    const hasCompanyFilter = filters.companyUuids.length > 0;
    const hasHostFilter = filters.hostUuids.length > 0;
    // Date filter is active if time frame is not 'today' (or null, which means default 'today') or if endDate is set
    const hasDateFilter = (selectedTimeFrame !== null && selectedTimeFrame !== 'today') || endDate !== null;

    return hasSearchFilter || hasStatusFilter || hasCompanyFilter || hasHostFilter || hasDateFilter;
  }, [filters, selectedTimeFrame, endDate]);

  const isEmpty = validVisits.length === 0;

  return {
    validVisits,
    isEmpty,
    areFiltersActive,
  };
};
